import React, { useContext, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mail,
  Phone,
  Calendar,
  Briefcase,
  GraduationCap,
  Edit3,
  Save,
  X,
  Camera,
  Upload,
  Edit,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import GoogleAccountConnect from "../../components/GoogleAccountConnect";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyTeacherProfile, updateTeacher } from "../../services/teachers";
import api from "../../services/api";
import toast from "react-hot-toast";
import ServiceWorkerSettings from "../../components/settings/ServiceWorkerSettings";

const TeacherProfile: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Photo upload state
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Fetch teacher profile
  const { data: teacherData, isLoading } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  // Update teacher mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      updateTeacher(teacherData?.data?._id || "", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-profile"] });
      setIsEditing(false);
      setEditData({});
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-emerald-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  const teacher = teacherData?.data;

  const handleEdit = () => {
    const deptValue = teacher?.department
      ? typeof teacher.department === "object"
        ? teacher.department._id
        : teacher.department
      : "";
    setEditData({
      department: deptValue,
      designation: teacher?.designation || "",
      emergencyContact: teacher?.emergencyContact || {},
      qualification: teacher?.qualification || {},
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleInputChange = (field: string, value: any) => {
    setEditData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedInputChange = (
    parent: string,
    field: string,
    value: any
  ) => {
    setEditData((prev: any) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  // Photo upload handlers
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo size should be less than 5MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;

    setIsUploadingPhoto(true);
    try {
      const photoFormData = new FormData();
      photoFormData.append("photo", photoFile);

      const photoResponse = await api.post("/upload/photo", photoFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if ((photoResponse as any).success && (photoResponse as any).data.url) {
        const photoUrl = (photoResponse as any).data.url;

        // Update the teacher's profile with the new photo URL
        await updateTeacher(teacherData?.data?._id || "", {
          profilePicture: photoUrl,
        });

        queryClient.invalidateQueries({ queryKey: ["teacher-profile"] });
        toast.success("Profile photo updated successfully!");

        // Reset photo state
        setPhotoFile(null);
        setPhotoPreview("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const removePhotoPreview = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-slate-900">Teacher Profile</h1>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="flex items-center px-4 py-2.5 bg-gradient-to-br from-emerald-300 to-emerald-400 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updateMutation.isPending ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              </>
            ) : (
              <button
                disabled
                onClick={handleEdit}
                className="flex items-center px-4 py-2.5 bg-gradient-to-br from-blue-300 to-indigo-400 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <div className="flex items-center mb-6">
            {/* Profile Picture Section */}
            <div className="relative mr-4">
              <div className="relative w-20 h-20">
                {teacher?.userInfo?.profileDetails?.profilePicture ||
                photoPreview ? (
                  <img
                    src={
                      photoPreview ||
                      teacher?.userInfo?.profileDetails?.profilePicture
                    }
                    alt={`${teacher?.userInfo?.firstName} ${teacher?.userInfo?.lastName}`}
                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback instanceof HTMLElement) {
                        fallback.classList.remove("hidden");
                      }
                    }}
                  />
                ) : null}
                <div
                  className={`${
                    teacher?.userInfo?.profileDetails?.profilePicture ||
                    photoPreview
                      ? "hidden"
                      : ""
                  } w-20 h-20 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full flex items-center justify-center shadow-lg border-4 border-white`}
                >
                  <span className="text-white font-bold text-2xl">
                    {teacher?.userInfo?.firstName?.charAt(0)}
                    {teacher?.userInfo?.lastName?.charAt(0)}
                  </span>
                </div>
              </div>

              {/* Photo upload overlay */}
              <div className="absolute -bottom-1 -right-1">
                <button
                  onClick={triggerFileInput}
                  className="w-8 h-8 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300 shadow-md"
                  title="Change profile picture"
                >
                  U
                </button>
              </div>

              {/* Photo preview actions */}
              {photoPreview && (
                <div className="absolute -top-2 -right-2 flex gap-1">
                  <button
                    onClick={handlePhotoUpload}
                    disabled={isUploadingPhoto}
                    className="w-7 h-7 bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300 disabled:opacity-50 shadow-md"
                    title="Upload photo"
                  >
                    <Upload className="h-3 w-3 text-white" />
                  </button>
                  <button
                    onClick={removePhotoPreview}
                    className="w-7 h-7 bg-gradient-to-br from-red-300 to-pink-400 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300 shadow-md"
                    title="Cancel"
                  >
                    <X className="h-3 w-3 text-white" />
                  </button>
                </div>
              )}
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />

            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                {teacher?.userInfo?.firstName} {teacher?.userInfo?.lastName}
              </h2>
              <p className="text-slate-600 font-medium">
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.designation || ""}
                    onChange={(e) =>
                      handleInputChange("designation", e.target.value)
                    }
                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Designation"
                  />
                ) : (
                  teacher?.designation || "Teacher"
                )}
                {" • "}
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.department || ""}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Department"
                  />
                ) : typeof teacher?.department === "object" ? (
                  teacher.department.name
                ) : (
                  teacher?.department || "Not specified"
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center text-slate-600 font-medium">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center mr-3 shadow-sm">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <span>{teacher?.userInfo?.email}</span>
            </div>
            <div className="flex items-center text-slate-600 font-medium">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mr-3 shadow-sm">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <span>
                {teacher?.userInfo?.profileDetails?.phone || "Not provided"}
              </span>
            </div>
            <div className="flex items-center text-slate-600 font-medium">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mr-3 shadow-sm">
                <Briefcase className="h-5 w-5 text-white" />
              </div>
              <span>Employee ID: {teacher?.employeeId}</span>
            </div>
            <div className="flex items-center text-slate-600 font-medium">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center mr-3 shadow-sm">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <span>
                Joined:{" "}
                {teacher?.joiningDate
                  ? new Date(teacher.joiningDate).toLocaleDateString()
                  : "Not specified"}
              </span>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">
            Emergency Contact
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Contact Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.emergencyContact?.name || ""}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "emergencyContact",
                      "name",
                      e.target.value
                    )
                  }
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium shadow-sm"
                  placeholder="Emergency contact name"
                />
              ) : (
                <p className="text-slate-600 font-medium">
                  {teacher?.emergencyContact?.name || "Not provided"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Relationship
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.emergencyContact?.relationship || ""}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "emergencyContact",
                      "relationship",
                      e.target.value
                    )
                  }
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium shadow-sm"
                  placeholder="Relationship"
                />
              ) : (
                <p className="text-slate-600 font-medium">
                  {teacher?.emergencyContact?.relationship || "Not provided"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Phone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editData.emergencyContact?.phone || ""}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "emergencyContact",
                      "phone",
                      e.target.value
                    )
                  }
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium shadow-sm"
                  placeholder="Emergency contact phone"
                />
              ) : (
                <p className="text-slate-600 font-medium">
                  {teacher?.emergencyContact?.phone || "Not provided"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={editData.emergencyContact?.email || ""}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "emergencyContact",
                      "email",
                      e.target.value
                    )
                  }
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium shadow-sm"
                  placeholder="Emergency contact email"
                />
              ) : (
                <p className="text-slate-600 font-medium">
                  {teacher?.emergencyContact?.email || "Not provided"}
                </p>
              )}
            </div>
          </div>
          {isEditing && (
            <div className="mt-4">
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Address
              </label>
              <textarea
                value={editData.emergencyContact?.address || ""}
                onChange={(e) =>
                  handleNestedInputChange(
                    "emergencyContact",
                    "address",
                    e.target.value
                  )
                }
                className="w-full border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium shadow-sm"
                rows={3}
                placeholder="Emergency contact address"
              />
            </div>
          )}
        </div>

        {/* Qualification & Experience */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center mr-3 shadow-sm">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            Qualification & Experience
          </h3>

          {/* Education */}
          <div className="mb-6">
            <h4 className="font-bold text-slate-900 mb-3">Education</h4>
            {teacher?.qualification?.education?.length > 0 ? (
              <div className="space-y-3">
                {teacher?.qualification?.education?.map(
                  (edu: any, index: number) => (
                    <div
                      key={index}
                      className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 shadow-sm"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-slate-900">
                            {edu.degree}
                          </p>
                          <p className="text-sm text-slate-600 font-medium">
                            {edu.institution}
                          </p>
                          <p className="text-sm text-slate-500 font-medium">
                            {edu.year} • Grade: {edu.grade || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className="text-slate-500 font-medium">
                No education information provided
              </p>
            )}
          </div>

          {/* Experience */}
          <div className="mb-6">
            <h4 className="font-bold text-slate-900 mb-3">Experience</h4>
            <p className="text-slate-600 font-medium">
              Total Years:{" "}
              {teacher?.qualification?.experience?.totalYears ||
                "Not specified"}
            </p>
            {teacher?.qualification?.experience?.previousPositions?.length >
              0 && (
              <div className="mt-3 space-y-3">
                {teacher?.qualification?.experience?.previousPositions?.map(
                  (pos: any, index: number) => (
                    <div
                      key={index}
                      className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 shadow-sm"
                    >
                      <p className="font-bold text-slate-900">{pos.position}</p>
                      <p className="text-sm text-slate-600 font-medium">
                        {pos.institution}
                      </p>
                      <p className="text-sm text-slate-500 font-medium">
                        {pos.startDate} - {pos.endDate || "Present"}
                      </p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          {/* Certifications */}
          <div>
            <h4 className="font-bold text-slate-900 mb-3">Certifications</h4>
            {teacher?.qualification?.certifications?.length > 0 ? (
              <div className="space-y-3">
                {teacher?.qualification?.certifications?.map(
                  (cert: any, index: number) => (
                    <div
                      key={index}
                      className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 shadow-sm"
                    >
                      <p className="font-bold text-slate-900">{cert.name}</p>
                      <p className="text-sm text-slate-600 font-medium">
                        {cert.issuingOrganization}
                      </p>
                      <p className="text-sm text-slate-500 font-medium">
                        Issued: {cert.issueDate}
                        {cert.expiryDate && ` • Expires: ${cert.expiryDate}`}
                      </p>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className="text-slate-500 font-medium">
                No certifications provided
              </p>
            )}
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">
            Bank Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Account Holder Name
              </label>
              <p className="text-slate-600 font-medium">
                {teacher?.bankDetails?.accountHolderName || "Not provided"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Bank Name
              </label>
              <p className="text-slate-600 font-medium">
                {teacher?.bankDetails?.bankName || "Not provided"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Account Number
              </label>
              <p className="text-slate-600 font-medium">
                {teacher?.bankDetails?.accountNumber
                  ? `****${teacher.bankDetails.accountNumber.slice(-4)}`
                  : "Not provided"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Branch Name
              </label>
              <p className="text-slate-600 font-medium">
                {teacher?.bankDetails?.branchName || "Not provided"}
              </p>
            </div>
          </div>
        </div>

        {/* Google Account Connection */}
        <GoogleAccountConnect />

        {/* App Settings Section */}
        <div className="mt-6">
          <ServiceWorkerSettings />
        </div>
      </div>
    </div>
  );
};

export default TeacherProfile;
